import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { sendValidatorEmail } from '../api/validator'
import { getMyEmails } from '../api/client'
import type { UserEmailOut } from '../api/users'

export interface EmailTarget {
  sessionId: number
  filename: string
  standard: string
  document_type: string
}

type ItemStatus = 'pending' | 'sending' | 'sent' | 'error'

interface SendItem extends EmailTarget {
  status: ItemStatus
  errorMsg?: string
}

interface Props {
  targets: EmailTarget[]
  onClose: () => void
}

function StepIcon({ status }: { status: ItemStatus }) {
  if (status === 'sent') {
    return (
      <span className="shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </span>
    )
  }
  if (status === 'sending') {
    return (
      <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
        <svg className="w-3.5 h-3.5 text-indigo-600 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span className="shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold">
        ✗
      </span>
    )
  }
  return (
    <span className="shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-400 text-xs font-semibold flex items-center justify-center">
      –
    </span>
  )
}

export function ValidatorEmailModal({ targets, onClose }: Props) {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<'confirm' | 'sending' | 'done' | 'error'>('confirm')
  const [items, setItems] = useState<SendItem[]>(
    targets.map(t => ({ ...t, status: 'pending' }))
  )
  const [toAddress, setToAddress] = useState<string | null>(null)
  const [emailAddresses, setEmailAddresses] = useState<UserEmailOut[]>([])
  const [selectedEmail, setSelectedEmail] = useState<string>('')
  const isSending = useRef(false)

  useEffect(() => {
    getMyEmails().then(list => {
      const approved = list.filter(e => e.status === 'approved')
      setEmailAddresses(approved)
      const primary = approved.find(e => e.is_primary) ?? approved[0]
      if (primary) setSelectedEmail(primary.email)
    }).catch(() => {})
  }, [])

  const sentCount = items.filter(i => i.status === 'sent').length
  const errorItems = items.filter(i => i.status === 'error')

  async function handleSend() {
    if (isSending.current) return
    isSending.current = true
    setPhase('sending')

    let lastTo: string | null = null
    let hasError = false

    for (const item of items) {
      setItems(prev => prev.map(i => i.sessionId === item.sessionId ? { ...i, status: 'sending' } : i))
      try {
        const result = await sendValidatorEmail(item.sessionId, selectedEmail || undefined)
        lastTo = result.to
        setItems(prev => prev.map(i => i.sessionId === item.sessionId ? { ...i, status: 'sent' } : i))
      } catch (err) {
        hasError = true
        const msg = err instanceof Error ? err.message : t('validator_email_modal.error_unknown')
        setItems(prev => prev.map(i => i.sessionId === item.sessionId ? { ...i, status: 'error', errorMsg: msg } : i))
      }
    }

    setToAddress(lastTo)
    isSending.current = false
    setPhase(hasError ? 'error' : 'done')
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && phase !== 'sending') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 animate-fadeIn">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden animate-fadeSlideIn">

        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {phase === 'confirm' && t('validator_email_modal.title_confirm', { count: items.length })}
            {phase === 'sending' && t('validator_email_modal.title_sending')}
            {phase === 'done' && t('validator_email_modal.title_done')}
            {phase === 'error' && t('validator_email_modal.title_partial')}
          </h2>
          {phase !== 'sending' && (
            <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* File list */}
        <div className="px-5 py-3 space-y-2 max-h-72 overflow-y-auto">
          {items.map(item => (
            <div key={item.sessionId} className="flex items-start gap-3 py-1.5">
              <StepIcon status={item.status} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate" title={item.filename}>
                  {item.filename}
                </div>
                <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                  [{item.standard}][{item.document_type}]
                </div>
                {item.status === 'error' && item.errorMsg && (
                  <div className="text-[11px] text-red-600 mt-0.5">{item.errorMsg}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
          {phase === 'confirm' && (
            <div className="space-y-3">
              {emailAddresses.length > 1 ? (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('validator_email_modal.send_to_label')}</label>
                  <select
                    value={selectedEmail}
                    onChange={e => setSelectedEmail(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {emailAddresses.map(e => (
                      <option key={e.id} value={e.email}>
                        {e.email}{e.label ? ` (${e.label})` : ''}{e.is_primary ? ' ★' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedEmail
                    ? <>{t('validator_email_modal.send_to_single_prefix')} <span className="font-medium">{selectedEmail}</span>.</>
                    : t('validator_email_modal.send_each_separate')}
                </p>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSend}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  {t('validator_email_modal.btn_send')}
                </button>
              </div>
            </div>
          )}

          {phase === 'sending' && (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {t('validator_email_modal.sending_hint')}
            </p>
          )}

          {(phase === 'done' || phase === 'error') && (
            <div className="flex items-center justify-between gap-3">
              <div>
                {sentCount > 0 && (
                  <p className="text-xs text-green-700 font-medium">
                    {t('validator_email_modal.sent_count', { count: sentCount })}{toAddress ? ` ${t('validator_email_modal.sent_to_suffix')} ${toAddress}` : ''}.
                  </p>
                )}
                {errorItems.length > 0 && (
                  <p className="text-xs text-red-600 mt-0.5">
                    {t('validator_email_modal.failed_count', { count: errorItems.length })}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shrink-0"
              >
                {t('common.close')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
